import React from "react";
import { motion } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { WHO_WE_ARE, COMPARISON } from "../../data/sparkseed";
import { Check, X } from "lucide-react";

export default function WhoWeAre() {
  return (
    <section
      data-testid="who-we-are-section"
      className="bg-[#14401F] text-white relative overflow-hidden"
    >
      {/* Decorative grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
              <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#B8D958" strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-10">
        <div className="max-w-3xl mb-14">
          <Eyebrow color="#B8D958">Who We Are</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold">
            Not a Typical <Italic className="text-[#B8D958]">VC</Italic>. Not a
            Fancy <Italic className="text-[#B8D958]">Accelerator</Italic>.
          </h2>
          <p className="mt-6 text-lg text-white/70">
            SparkSeed is a venture studio with the discipline of a fund and the
            hands of a builder.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-16">
          <div className="rounded-3xl p-8 bg-white/[0.04] border border-white/10 backdrop-blur-sm">
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] mb-5 font-semibold">
              We Are
            </div>
            <ul className="space-y-4">
              {WHO_WE_ARE.we_are.map((w, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 h-5 w-5 rounded-full bg-[#B8D958] flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-[#0F2818]" strokeWidth={3} />
                  </span>
                  <span className="text-white/90">{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl p-8 bg-white/[0.02] border border-white/5">
            <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5 font-semibold">
              We're Not
            </div>
            <ul className="space-y-4">
              {WHO_WE_ARE.we_arent.map((w, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 h-5 w-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <X className="h-3 w-3 text-white/50" strokeWidth={3} />
                  </span>
                  <span className="text-white/50 line-through decoration-white/20">
                    {w}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Comparison */}
        <div className="rounded-3xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-4 bg-white/[0.04] backdrop-blur-sm">
            <div className="p-5 text-xs uppercase tracking-[0.25em] text-white/40 font-semibold">
              Capability
            </div>
            <div className="p-5 text-center text-xs uppercase tracking-[0.25em] text-white/40 font-semibold">
              VC
            </div>
            <div className="p-5 text-center text-xs uppercase tracking-[0.25em] text-white/40 font-semibold">
              Accelerator
            </div>
            <div className="p-5 text-center text-xs uppercase tracking-[0.25em] text-[#B8D958] font-semibold bg-[#B8D958]/5">
              SparkSeed
            </div>
          </div>
          {COMPARISON.rows.map((row, i) => (
            <motion.div
              key={i}
              data-testid={`comparison-row-${i}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ delay: i * 0.1, duration: 0.45 }}
              className="grid grid-cols-4 border-t border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <div className="p-5 text-sm text-white/90 font-medium">{row}</div>
              <div className="p-5 text-center">
                {COMPARISON.matrix.VC[i] ? (
                  <Check className="h-5 w-5 text-[#B8D958] inline" />
                ) : (
                  <X className="h-5 w-5 text-white/20 inline" />
                )}
              </div>
              <div className="p-5 text-center">
                {COMPARISON.matrix.Accelerator[i] ? (
                  <Check className="h-5 w-5 text-[#B8D958] inline" />
                ) : (
                  <X className="h-5 w-5 text-white/20 inline" />
                )}
              </div>
              <div className="p-5 text-center bg-[#B8D958]/5">
                {COMPARISON.matrix.SparkSeed[i] ? (
                  <Check className="h-5 w-5 text-[#B8D958] inline" />
                ) : (
                  <X className="h-5 w-5 text-white/20 inline" />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
